import { isBlank, isNullOrUndefined } from "@/utils/ts-utils"
import { type IntlShape, useIntl } from "react-intl"

export const weekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const

export type TimeTableDelay = {
  appointmentOnly: boolean
  from: string
  to?: string
}

export type TimeTableData = {
  [k in (typeof weekDays)[number]]?: TimeTableDelay[]
}

export const useFormatTimeTable = (timetable?: TimeTableData) => {
  if (isNullOrUndefined(timetable) || Object.keys(timetable).length < 1) return []

  const intl = useIntl()
  const and = intl.formatMessage({ id: "and" })

  const ftt: string[] = []
  weekDays.map(d => {
    const ftd: string[] = []

    const tt = timetable[d] ?? []
    if (tt.length < 1) {
      ftt.push(
        `${intl.formatMessage({ id: `timetables.${d}` })} : ${intl?.formatMessage({ id: "closed" }) ?? "-"}`
      )
      return
    }

    tt.sort((a, b) => _compareFromHours(a, b)).map(ttd => {
      ftd.push(_renderDelayHours(ttd, intl))
    })
    ftt.push(
      `${intl.formatMessage({ id: `timetables.${d}` })} : ${ftd.join(", ").replace(/,([^,]+)$/, ` ${and}$1`)}`
    )
  })

  return ftt
}

// utils...

const _compareFromHours = (a: TimeTableDelay, b: TimeTableDelay) => {
  const [ah, am] = (a.from ?? "").split(":")
  const ad = new Date(0)
  ad.setUTCHours(Number.parseInt(ah ?? "0"))
  ad.setUTCMinutes(Number.parseInt(am ?? "0"))

  const [bh, bm] = (b.from ?? "").split(":")
  const bd = new Date(0)
  bd.setUTCHours(Number.parseInt(bh ?? "0"))
  bd.setUTCMinutes(Number.parseInt(bm ?? "0"))

  return ad.getTime() === bd.getTime() ? 0 : ad.getTime() - bd.getTime()
}

const _renderDelayHours = (timetabledelay?: TimeTableDelay, intl?: IntlShape) => {
  if (isNullOrUndefined(timetabledelay)) return intl?.formatMessage({ id: "closed" }) ?? "-"

  const appointmentOnly = timetabledelay.appointmentOnly === true

  if (isBlank(timetabledelay.from))
    return appointmentOnly
      ? (intl?.formatMessage({ id: "timetables.byRDVOnly" }) ?? "RDV")
      : (intl?.formatMessage({ id: "closed" }) ?? "-")

  const suffix = appointmentOnly
    ? ` (${intl?.formatMessage({ id: "timetables.byRDV" }) ?? "RDV"})`
    : ""

  if (isNullOrUndefined(intl)) {
    return (
      (isBlank(timetabledelay.to)
        ? `> ${timetabledelay.from}`
        : `${timetabledelay.from}-${timetabledelay.to}`) + suffix
    )
  }

  const [fh, fm] = timetabledelay.from.split(":")
  const [th, tm] = (timetabledelay.to ?? "").split(":")

  return (
    (isBlank(timetabledelay.to)
      ? intl
          .formatMessage({ id: "fromHour" }, { fh: _returnMinDigit(fh), fm: _returnTwoDigit(fm) })
          .toLocaleLowerCase()
      : intl
          .formatMessage(
            { id: "fromHourToHour" },
            {
              fh: _returnMinDigit(fh),
              fm: _returnTwoDigit(fm, true),
              th: _returnMinDigit(th),
              tm: _returnTwoDigit(tm, true),
            }
          )
          .toLocaleLowerCase()) + suffix
  )
}

const _returnMinDigit = (t = "") => {
  if (t.length < 2 || !t.match(/^[0-9]{1,2}$/)) return "0"
  if (t.length === 1) return t
  return t.replace(/^0/, "")
}

const _returnTwoDigit = (t = "", replace00byblank = false) => {
  if (replace00byblank && (isBlank(t) || `0${t}` === "00" || t === "00")) return ""
  if (t.length < 2 || !t.match(/^[0-9]{1,2}$/)) return "00"
  if (t.length === 1) return `0${t}`
  return t
}
